import { useState } from "react";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import { getBackendUrl } from "../../../../utils/env";
import { buildPrintHTML } from "../utils/pdfTemplate";
import { buildQuotationBodyHTML } from "../utils/quotationPdfContent";

const toBase64FromPublic = async (path) => {
  try {
    const response = await fetch(path);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error converting image to base64:", error);
    return "";
  }
};

export const useQuotationExport = ({
  token_client,
  specialist,
  email,
  phone,
  selectedServices,
  visibleTiers,
  calculateTotal,
  calculateTotalVat,
  calculateFinalTotal,
}) => {
  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const buildQuotationDocument = async () => {
    const date = new Date().toLocaleDateString("vi-VN");
    const subtotal = calculateTotal();
    const vatTotal = calculateTotalVat();
    const grandTotal = calculateFinalTotal();

    const bodyHTML = buildQuotationBodyHTML({
      date,
      specialist,
      email,
      phone,
      selectedServices,
      visibleTiers,
      subtotal,
      vatTotal,
      grandTotal,
    });

    const [regularFontBase64, boldFontBase64] = await Promise.all([
      toBase64FromPublic("/fonts/NotoSerif-Regular.ttf"),
      toBase64FromPublic("/fonts/NotoSerif-Bold.ttf"),
    ]);
    const html = buildPrintHTML(bodyHTML, "Báo giá dịch vụ", {
      regularFontBase64,
      boldFontBase64,
    });
    return { html, date };
  };

  const generatePdf = async () => {
    const { html, date } = await buildQuotationDocument();
    const watermarkBase64 = await toBase64FromPublic("/images/watermark-45.png");
    const BE_URL = getBackendUrl();

    const response = await fetch(`${BE_URL}/pdf/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token_client}`,
      },
      body: JSON.stringify({
        html,
        profileName: "Báo giá dịch vụ",
        watermarkBase64,
        watermarkOpacity: 0.12,
        options: { width: 794, height: 1123, deviceScaleFactor: 2 },
        watermarkMode: "fixed",
      }),
    });

    if (!response.ok) throw new Error("Không thể tạo PDF");
    return { blob: await response.blob(), date };
  };

  const generatePreviewImage = async () => {
    const { html } = await buildQuotationDocument();
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.left = "-99999px";
    iframe.style.top = "0";
    iframe.style.width = "794px";
    iframe.style.height = "1123px";
    iframe.style.border = "0";
    iframe.style.background = "#ffffff";
    document.body.appendChild(iframe);

    try {
      iframe.srcdoc = html;
      await new Promise((resolve) => {
        iframe.onload = () => resolve();
      });
      await new Promise((resolve) => setTimeout(resolve, 250));

      const iframeDocument = iframe.contentDocument;
      if (!iframeDocument) throw new Error("Không thể tạo xem trước");
      if (iframeDocument.fonts?.ready) {
        await iframeDocument.fonts.ready;
      }

      const canvas = await html2canvas(iframeDocument.body, {
        backgroundColor: "#ffffff",
        scale: 1.5,
        useCORS: true,
        width: 794,
        height: 1123,
        windowWidth: 794,
        windowHeight: 1123,
      });

      return canvas.toDataURL("image/png");
    } finally {
      iframe.remove();
    }
  };

  const exportQuotation = async () => {
    if (!selectedServices || selectedServices.length === 0) {
      toast.error("Vui lòng chọn ít nhất một dịch vụ");
      return;
    }

    try {
      setExporting(true);
      const { blob, date } = await generatePdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bao-gia-${date.replace(/\//g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Xuất file thành công");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Xuất file thất bại");
    } finally {
      setExporting(false);
    }
  };

  const previewQuotation = async () => {
    if (!selectedServices || selectedServices.length === 0) {
      toast.error("Vui lòng chọn ít nhất một dịch vụ");
      return null;
    }

    try {
      setPreviewing(true);
      return await generatePreviewImage();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Xem trước thất bại");
      return null;
    } finally {
      setPreviewing(false);
    }
  };

  return { exporting, previewing, exportQuotation, previewQuotation };
};
